// Import WordPress libraries
import { registerBlockVariation } from '@wordpress/blocks';
import { addFilter } from '@wordpress/hooks';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToggleControl, TextControl, RadioControl, RangeControl, SelectControl, FormTokenField,  BaseControl } from '@wordpress/components';
import { useState } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { __ } from '@wordpress/i18n';

// Import and set Embedded Posts variables
import EPIcon from './icon';
const EP = 'cooperativeit/embedded-posts';

// Registering Block Variation for Embedded Posts
registerBlockVariation('core/query', {
    name: EP,
    title: __( 'Embedded Posts', 'cooperativeit/embedded-posts' ),
    description:  __( 'This plugin displays related posts via category, tags, author or title using either a Gutenberg block or a WordPress shortcode.', 'cooperativeit/embedded-posts' ), 
    isActive: ['namespace'],
    icon: EPIcon,
    attributes: {
        namespace: EP,
        query: {
            postType: 'post',
            perPage: 5,
            offset: 0,
            order: 'asc',
            orderBy: 'date'
        },
    },

    allowedControls: [],
    scope: [ 'inserter' ],
    innerBlocks: [
        [
            'core/post-template',
            {},
            [['core/post-title'], ['core/post-excerpt']],
        ],
        ['core/query-pagination'],
        ['core/query-no-results'],
    ],  
});



// Checks if Embedded Posts is active or not
const isEmbeddedPosts = (props) => {
    const {
        attributes: { namespace },
    } = props;
    return namespace && namespace === EP;
};

// Embedded Posts Settings Panel
const EmbeddedPostsSettings = ({ props }) => {
    const [ ,setSelectedContinents ] = useState( [] );

    const postTypes = useSelect((select) => {
        const allPostTypes = select( 'core' ).getPostTypes( { per_page: -1 })
        return allPostTypes?.filter(({ viewable }) => viewable)?.map(({ slug }) => slug);
    });

    const currentPostType = props.attributes.query['postType'];
    let additionalPostTypes = props.attributes.query['additionalPostTypes'] || [];

    if (additionalPostTypes.length === 0) {
        additionalPostTypes = [currentPostType]; // Wrap it in an array if needed
    }

    // Set operator to AND as default if both related by tags and cats is selected
    if(!props.attributes.query['relatedByOperator']){
        props.attributes.query['relatedByOperator'] = 'AND';
    }

    const { query } = props.attributes;

    return (
        <PanelBody title= {__( "Embedded Posts Settings", 'cooperativeit/embedded-posts' )} initialOpen={!props.attributes.query['searchByPostID'] && !props.attributes.query['searchByTitle']}>
            <BaseControl
			    help={ 
                    __( 
			    	'WordPress contains different types of content and they are divided into collections called “Post types”. You have the flexibility to modify the post type for searching or add additional post types, enabling simultaneous searching across multiple post types.'
                    , 'cooperativeit/embedded-posts' )
                }>

                <FormTokenField
                    label={__( "Post Types", 'cooperativeit/embedded-posts' )}
                    value={additionalPostTypes}
                    suggestions={postTypes?.filter((type) => type !== additionalPostTypes && type !== 'attachment')}

                    onChange={(tokens) => {
                        setSelectedContinents(tokens);

                        props.setAttributes({
                            query: {
                                ...props.attributes.query,
                                'additionalPostTypes': tokens
                            },
                            reloadTrigger: Date.now() // Update reloadTrigger here
                        });
                    }}
                    __experimentalExpandOnFocus
                />

            </BaseControl>

            {!query.relatedByRandom && 
            <SelectControl
                label={__( "Order By", 'cooperativeit/embedded-posts' )}
                value={props.attributes.query['orderBy'] + '/' + props.attributes.query['order']}
                options={[
                    { label: __( 'Newest to oldest', 'cooperativeit/embedded-posts' ), value: 'date/desc' },
                    { label: __( 'Oldest to newest', 'cooperativeit/embedded-posts' ), value: 'date/asc' },
                    { label: __( 'A → Z', 'cooperativeit/embedded-posts' ), value: 'title/asc' },
                    { label: __( 'Z → A', 'cooperativeit/embedded-posts' ), value: 'title/desc' },
                ]}
                onChange={(value) => {

                    // Split the value using '/'
                    const [orderBy, order] = value.split('/');

                    // Update the attributes
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'orderBy': orderBy,
                            'order': order,
                        },
                    });
                }}
            />}

            <ToggleControl
                label={__( "Random Ordering", 'cooperativeit/embedded-posts' )}
                checked={ props.attributes.query['relatedByRandom'] }
                help={__( 'This will display a random ordering of the posts.', 'cooperativeit/embedded-posts' )}
                onChange={(random) => {
                
                    // Prepare the new attributes based on the condition
                    const newAttributes = {
                        ...props.attributes.query,
                        'relatedByRandom': random
                    };
                
                    // If random is false, add or update other attributes to trigger a re-render
                    if (!random) {
                        newAttributes.reloadTrigger = Date.now(); // Use a unique value to trigger re-render
                    }
                
                    // Set the attributes
                    props.setAttributes({
                        query: newAttributes,
                    });
                }}
                
                
            />
            <RangeControl
                label={__( "Items Per Page", 'cooperativeit/embedded-posts' )}
                value={ props.attributes.query['perPage'] }
                onChange={(perpage) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'perPage': perpage,
                        },
                    });
                }}
                min={ 1 }
                max={ 10 }
            />
        </PanelBody>
    );
};

// Relate By Settings Panel
const RelatedBy = ({ props }) => {
    return (
        <PanelBody title={__( "Related By", 'cooperativeit/embedded-posts' )}>
            <ToggleControl
                label={__( "Categories", 'cooperativeit/embedded-posts' )}
                checked={ props.attributes.query['relatedByCategory'] }
                onChange={ (category) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByCategory': category,
                        },
                    });
                } }
            />
            <ToggleControl
                label={__( "Tags", 'cooperativeit/embedded-posts' )}
                checked={ props.attributes.query['relatedByTag'] }
                onChange={ (tag) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByTag': tag,
                        },
                    });
                } }
            />
            {
                props.attributes.query['relatedByCategory'] &&
                props.attributes.query['relatedByTag'] && (
                        <RadioControl
                            label={__( "Taxonomy Operator", 'cooperativeit/embedded-posts' )}
                            help={
                                <div>
                                    {__('AND will select posts related by both Tags and Categories.', 'cooperativeit/embedded-posts')}
                                    <br />
                                    {__('OR will select posts related by either Tags and Categories.', 'cooperativeit/embedded-posts')}
                                </div>
                            }
                            selected={props.attributes.query['relatedByOperator']}
                            options={[
                                { label: __( 'AND', 'cooperativeit/embedded-posts' ), value: 'AND' },
                                { label: __( 'OR', 'cooperativeit/embedded-posts' ), value: 'OR' },
                            ]}
                            onChange={(operator) => {
                                props.setAttributes({
                                    query: {
                                        ...props.attributes.query,
                                        'relatedByOperator': operator,
                                    },
                                });
                            }}
                        />
                )
            }
            <ToggleControl
                label={__( "Author", 'cooperativeit/embedded-posts' )}
                checked={ props.attributes.query['relatedByAuthor'] }
                onChange={ (author) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByAuthor': author,
                        },
                    });
                } }
            />
        </PanelBody>
    );
};

// Single Posts Settings Panel
const SinglePost = ({ props }) => {

    const decodeHtml = (html) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    };


    const [selectedTitles, setSelectedTitles] = useState([]);

    const { posts } = useSelect((select) => {
        const { getEntityRecords, getPostTypes } = select('core');

        // Retrieve all available post types
        const postTypes = getPostTypes({ per_page: -1 });
 
        if (!postTypes) return { posts: [] };

        // For each post type, retrieve the posts and combine them
        const query = {
            per_page: -1,
            viewable: true
        };

        // Remove theme parts from the posts array
        const allowedPostTypes = postTypes.filter((postType) => 
            !['wp_template', 'wp_template_part'].includes(postType.slug) // Add slugs of unwanted types here
        );
        
        const allPosts = allowedPostTypes.flatMap((postType) => 
            getEntityRecords('postType', postType.slug, query) || []
        );
        return { posts: allPosts };
        }, []);

        // Map posts to titles for use in options
        let options = [];
        if (posts) {
            options = posts.map((post) => decodeHtml(post.title.raw)); // Use 'rendered' to get the title
        }

    return (
        <PanelBody title={__( "Single Post", 'cooperativeit/embedded-posts' )}>
            <FormTokenField
                label={__( "Title", 'cooperativeit/embedded-posts' )}
                value={ props.attributes.query['searchByTitle'] }
                suggestions={ options }
                maxLength={ 1 }
                onChange={(tokens) => {
                    setSelectedTitles(tokens);

                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'searchByTitle': tokens
                        },
                        reloadTrigger: Date.now() // Update reloadTrigger here
                    });
                }}
                __experimentalShowHowTo={ false }
            />

            <TextControl
                label={__( "Post ID", 'cooperativeit/embedded-posts' )}
                value={ props.attributes.query['searchByPostID'] }
                onChange={(searchID) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'searchByPostID': searchID,
                        },
                    });
                }}
            />
        </PanelBody>
    );
};

// Enable all setting panels
export const embeddedPostsControls = (BlockEdit) => (props) => {

    return isEmbeddedPosts(props) ? (
        <>
            <BlockEdit {...props} />
            <InspectorControls>
                <EmbeddedPostsSettings props={props} />
                <RelatedBy props={props} />
                <SinglePost props={props} />
            </InspectorControls>
        </>
    ) : (
        <BlockEdit {...props} />
    );
};

// Adding Filter for Block Edit to Include Custom Controls
addFilter('editor.BlockEdit', 'core/query', embeddedPostsControls);
